/**
 * Markdown for articles, turned into the blocks the marketing site renders
 * and the console previews. Deliberately small: headings, paragraphs,
 * bulleted lists, quotes, fenced code, pictures and video. Inline emphasis
 * (`**bold**`, `_italic_`, `` `code` ``, links) is kept as text and rendered
 * by the site's inline renderer.
 *
 * Pictures: `![caption](asset:ID)` or `![caption](https://…)`.
 * Video:    `!video[caption](https://www.youtube.com/watch?v=…)`,
 *           `!video[caption](https://vimeo.com/…)`, `!video[caption](asset:ID)`,
 *           or a YouTube or Vimeo link on a line of its own.
 * `asset:ID` refers to a file uploaded from the console; the caller maps it
 * to a URL with `assetUrl`.
 */
export type ArticleBlock =
  | { type: 'heading'; text: string; level: 2 | 3 }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[]; ordered?: boolean }
  | { type: 'quote'; text: string }
  | { type: 'code'; text: string; lang?: string }
  | { type: 'image'; src: string; alt: string; caption?: string }
  | { type: 'video'; src: string; provider: 'youtube' | 'vimeo' | 'file'; caption?: string };

export interface MarkdownOptions {
  /** Turns `asset:ID` into a fetchable URL. Defaults to `/api/blog-assets/ID`. */
  assetUrl?: (id: string) => string;
}

const YOUTUBE = /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;
const VIMEO = /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/i;
const IMAGE = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const VIDEO = /^!video\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const BARE_LINK = /^(https?:\/\/\S+)\s*$/;

function resolve(src: string, opts: MarkdownOptions): string {
  const m = /^asset:([0-9a-fA-F-]{36})$/.exec(src.trim());
  if (!m) return src.trim();
  return (opts.assetUrl ?? ((id) => `/api/blog-assets/${id}`))(m[1]);
}

/** The embed form of a video link, and which player it needs. */
export function videoSource(url: string, opts: MarkdownOptions = {}): { src: string; provider: 'youtube' | 'vimeo' | 'file' } {
  const yt = YOUTUBE.exec(url);
  if (yt) return { src: `https://www.youtube-nocookie.com/embed/${yt[1]}`, provider: 'youtube' };
  const vm = VIMEO.exec(url);
  if (vm) return { src: `https://player.vimeo.com/video/${vm[1]}`, provider: 'vimeo' };
  return { src: resolve(url, opts), provider: 'file' };
}

export function markdownToBlocks(markdown: string, opts: MarkdownOptions = {}): ArticleBlock[] {
  const lines = (markdown ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ArticleBlock[] = [];
  let para: string[] = [];
  let list: { items: string[]; ordered: boolean } | null = null;
  let quote: string[] = [];
  let code: { lang?: string; lines: string[] } | null = null;

  const flushPara = () => { if (para.length) { blocks.push({ type: 'paragraph', text: para.join(' ').trim() }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ type: 'list', items: list.items, ordered: list.ordered || undefined }); list = null; } };
  const flushQuote = () => { if (quote.length) { blocks.push({ type: 'quote', text: quote.join(' ').trim() }); quote = []; } };
  const flushAll = () => { flushPara(); flushList(); flushQuote(); };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');

    if (code) {
      if (/^```/.test(line)) { blocks.push({ type: 'code', text: code.lines.join('\n'), lang: code.lang }); code = null; }
      else code.lines.push(raw);
      continue;
    }
    const fence = /^```\s*([A-Za-z0-9_-]*)\s*$/.exec(line);
    if (fence) { flushAll(); code = { lang: fence[1] || undefined, lines: [] }; continue; }

    if (!line.trim()) { flushAll(); continue; }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) { flushAll(); blocks.push({ type: 'heading', text: heading[2].trim(), level: heading[1].length <= 2 ? 2 : 3 }); continue; }

    const video = VIDEO.exec(line);
    if (video) { flushAll(); blocks.push({ type: 'video', ...videoSource(video[2], opts), caption: video[1].trim() || undefined }); continue; }

    const image = IMAGE.exec(line);
    if (image) { flushAll(); blocks.push({ type: 'image', src: resolve(image[2], opts), alt: image[1].trim(), caption: image[1].trim() || undefined }); continue; }

    const bare = BARE_LINK.exec(line);
    if (bare && (YOUTUBE.test(bare[1]) || VIMEO.test(bare[1]))) { flushAll(); blocks.push({ type: 'video', ...videoSource(bare[1], opts) }); continue; }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushPara(); flushQuote();
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) { flushList(); list = { items: [], ordered }; }
      list.items.push((bullet ?? numbered)![1].trim());
      continue;
    }

    const quoted = /^>\s?(.*)$/.exec(line);
    if (quoted) { flushPara(); flushList(); quote.push(quoted[1]); continue; }

    flushList(); flushQuote();
    para.push(line.trim());
  }
  if (code) blocks.push({ type: 'code', text: code.lines.join('\n'), lang: code.lang });
  flushAll();
  return blocks;
}

/** A plain-text excerpt from Markdown, for a list card when the author left the excerpt blank. */
export function markdownExcerpt(markdown: string, maxChars = 180): string {
  const first = markdownToBlocks(markdown).find((b) => b.type === 'paragraph') as { text: string } | undefined;
  const text = (first?.text ?? '').replace(/[*_`]/g, '');
  return text.length <= maxChars ? text : `${text.slice(0, maxChars - 1).trimEnd()}…`;
}
